import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { Select } from '../../../../components/Select';
import { Textarea } from '../../../../components/Textarea';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import { SetupUsergroup } from '../../../../models/SetupUsergroup';
import { useSetupUsergroup } from '../../../../hooks/useSetupUsergroup';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import Loading from '../../../../components/Loading';
import { useWarehouses } from '../../../../hooks/useWarehouse';

const SetupEditCustomerPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // Assuming `id` is passed as a URL parameter
    const [customer, setCustomer] = useState<SetupUsergroup | null>(null);
    const { updateUserGroup, fetchUserGroupById, loading, error } = useSetupUsergroup();
    const { warehouses } = useWarehouses();
    const filteredWarehouses = warehouses.filter(warehouse => warehouse.hq !== '1');

    useEffect(() => {
        document.title = "Edit Customer";
    }, []);

    useEffect(() => {
        const fetchCustomerDetails = async () => {
            if (!id) return;

            try {
                const fetchedcustomer = await fetchUserGroupById(Number(id));
                setCustomer(fetchedcustomer);
            } catch (err) {
                showErrorToast('Failed to fetch customer details.');
            }
        };

        fetchCustomerDetails();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCustomer(prev => prev ? ({ ...prev, [name]: value }) : null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!customer) return;
        if (!customer.fullname || !customer.account_code || !customer.email || !customer.address || !customer.warehouse_id) {
            showErrorToast("Please fill in all field before submit");
            return;
        }
        try {
            await updateUserGroup(Number(customer.id!), customer);
            showLoadingToast(`Updating ${customer.fullname}...`, 100);
            setTimeout(() => {
                navigate('/gp/setup/customers');
                showSuccessToast(`${customer.fullname} updated successfully`);
            }, 1000);
        } catch (err) {
            showErrorToast('Failed to update customer.');
        }
    };

    if (loading) return <Loading />;
    if (error) return <p>{error}</p>;
    if (!customer) return <p>customer not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit User</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3" disabled={(!customer.fullname || !customer.account_code || !customer.email || !customer.address || !customer.warehouse_id)}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Profile</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Details about individuals or businesses purchasing goods or services.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Full Name</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Full Name"
                                        name="fullname"
                                        value={customer.fullname}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Second Name</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Second Name"
                                        name="second_name"
                                        value={customer.second_name}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Area</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Area"
                                        name="area"
                                        value={customer.area}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Group</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Group"
                                        name="group"
                                        value={customer.group}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Office Number</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Office Number"
                                        name="office_no"
                                        value={customer.office_no?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Fax Number</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Fax Number"
                                        name="fax_no"
                                        value={customer.fax_no?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Mobile Number</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Mobile Number"
                                        name="mobile_no"
                                        value={customer.mobile_no?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Account Code</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Account Code"
                                        name="account_code"
                                        value={customer.account_code?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Address</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Address"
                                        name="address"
                                        value={customer.address?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Address 2</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Address 2"
                                        name="address_2"
                                        value={customer.address_2?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Address 3</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Address 3"
                                        name="address_3"
                                        value={customer.address_3?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Address 4</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Address 4"
                                        name="address_4"
                                        value={customer.address_4?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Email</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        type="email"
                                        aria-label="Email"
                                        name="email"
                                        value={customer.email?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Warehouse</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Warehouse"
                                        name="warehouse_id"
                                        value={customer.warehouse_id!}
                                        onChange={handleChange}
                                    >
                                        {filteredWarehouses.map(warehouse => (
                                            <option key={warehouse.id.toString()} value={warehouse.id.toString()}>
                                                {warehouse.warehouse_name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={(!customer.fullname || !customer.account_code || !customer.email || !customer.address || !customer.warehouse_id)}>Save</Button>
            </div>
        </form>
    );
};

export default SetupEditCustomerPage;
